/**
 * React Query hook for the Leads dashboard.
 *
 * The dashboard query is heavy (8 parallel aggregations) and is also
 * cached server-side for 30 s. React Query keeps a per-tab cache so
 * repeated tab focus / route changes don't re-hit the backend.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api, { LeadsDashboardMetrics } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function useDashboard(pipelineId?: string) {
  const query = useQuery<LeadsDashboardMetrics>({
    queryKey: queryKeys.leads.dashboard(pipelineId),
    queryFn: () => api.getLeadsDashboardMetrics(pipelineId),
    // Server caches 30 s; we mirror that on the client.
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.isError && !query.isFetching) {
      toast({ title: 'Error', description: 'Failed to load dashboard', variant: 'destructive' });
    }
  }, [query.isError, query.isFetching]);

  return {
    metrics: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}
